import { useCallback, useMemo } from "react";

import type { ColDef } from "ag-grid-community";
import { unlink as unlinkTuPart } from "@synthesis/shared-services/internal/application/client";
import { downloadTuPartFile } from "@synthesis/shared-services";
import { setPromiseSnack, setSnack, Table } from "@synthesis/shared-ui";
import { cn, useDownload } from "@synthesis/shared-utils";

import { useTuParts } from "../../hooks/use-tu-parts";
import { usePageStore } from "../../application-detail-page.lib";
import { DetailTabPlaceholder } from "../detail-tab-placeholder";
import { TuPartActionsCell } from "./tu-part-actions-cell";
import { TuPartFileNameCell } from "./tu-part-file-name-cell";
import { formatUploadDate, type TuPartRow } from "./tu-parts.lib";

type TuPartsContentProps = {
  requestId: string;
  canUpload: boolean;
  canDelete: boolean;
  className?: string;
};

const SNACK_TITLES_DELETE = {
  pending: "Выполняется удаление документа...",
  fulfilled: "Документ удален",
  rejected: "Не удалось удалить документ. Попробуйте еще раз",
} as const;

const TU_PART_TABLE_COLUMNS: ColDef<TuPartRow>[] = [
  {
    headerName: "Подразделение",
    colId: "departmentName",
    field: "departmentName",
    width: 180,
  },
  {
    headerName: "Документ",
    colId: "fileName",
    cellRenderer: TuPartFileNameCell,
    cellClass: "flex items-center",
    flex: 1,
    minWidth: 260,
  },
  {
    headerName: "Дата загрузки",
    colId: "createdAt",
    field: "createdAt",
    valueFormatter: ({ value }) => formatUploadDate(String(value ?? "")),
    width: 180,
  },
  {
    headerName: "Автор",
    colId: "authorName",
    field: "authorName",
    flex: 1,
    minWidth: 140,
  },
  {
    headerName: "",
    colId: "actions",
    cellRenderer: TuPartActionsCell,
    cellClass: "flex items-center justify-center",
    width: 100,
    sortable: false,
    filter: false,
    suppressHeaderMenuButton: true,
  },
];

export const TuPartsContent = ({ requestId, canUpload, canDelete, className }: TuPartsContentProps) => {
  const pageStore = usePageStore();
  const downloadFile = useDownload();
  const { tuParts, fileMetaMap, authorsMap, formatFileName, isLoading, isError, invalidateTuParts } = useTuParts(requestId);

  const handleDownload = useCallback(
    async (part: TuPartRow) => {
      if (!part.fileId) {
        return;
      }

      const promise = async () => {
        const blob = await downloadTuPartFile({ requestId, fileId: part.fileId as string });
        downloadFile(
          {
            id: part.fileId as string,
            baseName: part.baseName ?? part.fileName ?? "Часть ТУ",
            ...(part.ext ? { ext: part.ext } : {}),
          },
          blob,
        );
      };

      try {
        await promise();
      } catch {
        setSnack({
          messageType: "error",
          title: "Не удалось скачать файл",
        });
      }
    },
    [downloadFile, requestId],
  );

  const handleDelete = useCallback(
    async (part: TuPartRow) => {
      if (!part.fileId) {
        return;
      }

      const promise = (async () => {
        await unlinkTuPart({ path: { requestId, fileId: part.fileId as string } });
        await invalidateTuParts();
      })();

      await setPromiseSnack({ promise, promiseSettings: { title: SNACK_TITLES_DELETE } });
    },
    [invalidateTuParts, requestId],
  );

  const handleUpload = useCallback(
    (part: TuPartRow) => {
      pageStore?.setPopupAction({ type: "upload-tu-part", requestId, departmentId: part.departmentId });
    },
    [pageStore, requestId],
  );

  const rows = useMemo<TuPartRow[]>(
    () =>
      tuParts.map((part) => {
        const fileMeta = part.fileId ? fileMetaMap.get(part.fileId) : undefined;
        const authorId = fileMeta?.author;

        return {
          ...part,
          fileName: fileMeta ? formatFileName(fileMeta) : null,
          baseName: fileMeta?.baseName ?? null,
          ext: fileMeta?.ext ?? null,
          createdAt: fileMeta?.createdAt ?? null,
          authorId: authorId ?? null,
          authorName: authorId ? (authorsMap.get(authorId)?.name ?? "—") : null,
        };
      }),
    [tuParts, fileMetaMap, authorsMap, formatFileName],
  );

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <div className="flex flex-col gap-1">
        <h3 className="subtitle-lg text-type-common">Части технических условий</h3>
        <p className="subtitle-sm text-type-minor">Части технических условий, полученные от региональных подразделений</p>
      </div>

      {isError ? (
        <p className="paragraph-sm text-type-error py-4 text-center">Не удалось загрузить части ТУ</p>
      ) : tuParts.length === 0 ? (
        <DetailTabPlaceholder
          title="Региональные подразделения не назначены"
          description="После назначения подразделений информация появится в таблице."
        />
      ) : (
        <Table
          className="w-full"
          tableId="tu-parts-table"
          tableColumns={TU_PART_TABLE_COLUMNS}
          tableData={rows}
          getRowId={({ data }: { data?: TuPartRow }) => data?.departmentId ?? ""}
          context={{ canUpload, canDelete, onDownload: handleDownload, onDelete: handleDelete, onUpload: handleUpload }}
          isLoading={isLoading}
          isSortable={false}
          hasFilters={false}
          suppressHeaderContextMenu
          domLayout="autoHeight"
        />
      )}
    </div>
  );
};
